<template>
  <v-container>
    <v-card class="ma-4 pa-4">
      <v-card-title>Purchase a gift card</v-card-title>
      <v-progress-linear v-if="loadingItems" indeterminate />
      <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{
        error
      }}</v-alert>
      <v-select
        v-if="items.length > 1"
        v-model="selectedId"
        label="Gift card"
        :items="items"
        item-title="itemName"
        item-value="instanceId"
      />
      <CurrencyInput
        v-if="selected?.allowPriceOverride"
        v-model:amount-cents="amount"
        label="Amount"
        required
      />
      <p v-else-if="selected" class="mb-4">
        {{ formatRawCost(selected.priceCents ?? 0) }}
      </p>
      <PurchaserInfo
        @submit="submit"
        submit-text="Add to cart"
        :loading="loading"
        :disabled="!selected || !session?.canEdit.value"
        :validate-purchase="validatePurchase"
      />
      <v-btn
        v-if="
          session?.orderRef.value && !session.ready.value && !session.busy.value
        "
        class="mt-4"
        @click="reloadCart"
        >Reload cart</v-btn
      >
    </v-card>
  </v-container>
</template>
<script lang="ts" setup>
import { useCatalog } from "@/composables/shop/useCatalog";
import type { ProductListing } from "@/api/models/shop/ProductListing";
import { BalanceType } from "../../../cbi-generator/generated/dto/BalanceType";
import type { ConfigureDelivery } from "../../../cbi-generator/generated/dto/ConfigureDelivery";
import { buildMemberPurchase } from "@/composables/shop/memberPurchase";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useUtility } from "@/composables/useUtility";
import PurchaserInfo from "./PurchaserInfo.vue";
import { errorMessage } from "@/composables/generated/contracts";

const router = useRouter();
const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
const { formatRawCost } = useUtility();
const items = ref<ProductListing[]>([]);
const selectedId = ref<number>();
const selected = computed(() =>
  items.value.find((item) => item.instanceId === selectedId.value),
);
const amount = ref(0);
const loading = ref(false);
const loadingItems = ref(true);
const error = ref("");
onMounted(async () => {
  try {
    items.value = (await useCatalog()).products.filter(
      (item) =>
        item.isGiftCardProduct() &&
        (item.catalogItem.balanceType == null ||
          item.catalogItem.balanceType === BalanceType.GIFT_CARD),
    );
    if (!items.value.length)
      throw new Error("No gift cards are currently on sale.");
    selectedId.value = items.value[0].instanceId;
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    loadingItems.value = false;
  }
});
async function reloadCart() {
  try {
    await session?.refresh();
  } catch (cause) {
    error.value = errorMessage(cause);
  }
}
function validatePurchase() {
  if (!selected.value) throw new Error("Choose an available gift card.");
  buildMemberPurchase(selected.value, amount.value);
}
async function submit(delivery: ConfigureDelivery) {
  if (!session || !selected.value || loading.value) return;
  loading.value = true;
  error.value = "";
  try {
    await session.addPurchase(
      buildMemberPurchase(selected.value, amount.value),
      delivery,
    );
    await router.push({ name: "portal-cart" });
  } catch (cause) {
    error.value = errorMessage(cause);
  } finally {
    loading.value = false;
  }
}
</script>
